import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import Image from '../image'
import {hasValidAsset, hasValidImageSrc, isVideoAsset} from '../../../utils/utils'
import Video from '../video'
import VideoControl from '../video-control'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import useVideoControl from '../../../hooks/useVideoControl'
import {assetSlotRecipe} from './index.recipe'

/**
 * Asset component to display image or video
 *
 * @component
 * @param {object} [asset] - Optional asset object
 * @param {object|function} [videoRef] - Ref object or callback for the video element
 * @returns {JSX.Element|null} Rendered asset or null if no asset provided
 */
const Asset = ({asset, videoRef, loading, widths, objectFit, variant = 'default'}) => {
    const [isPlaying, onVideoToggle] = useVideoControl(asset?.autoplay, videoRef)

    const recipe = useSlotRecipe({recipe: assetSlotRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    if (!hasValidAsset(asset)) return null

    if (isVideoAsset(asset))
        return (
            <Box {...styles.container}>
                <Video ref={videoRef} {...asset} loading={loading} widths={widths} />
                <Box {...styles.bottomGradient}></Box>
                <Box {...styles.videoControlBox}>
                    <VideoControl isPlaying={isPlaying} onToggle={onVideoToggle} />
                </Box>
            </Box>
        )

    if (hasValidImageSrc(asset))
        return (
            <Image
                {...asset}
                loading={loading}
                objectFit={objectFit}
                fetchpriority={loading === 'eager' ? 'high' : undefined}
                widths={widths}
                // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                // hero image download over other resources. Combined with loading="eager" and
                // preload, this ensures fastest possible above-the-fold render.
            />
        )
}

Asset.propTypes = {
    asset: PropTypes.object,
    videoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    loading: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    objectFit: PropTypes.string,
    variant: PropTypes.string
}

export default Asset
